export const WECHAT_QR_EXPIRED_CODE = 10129
export const WECHAT_QR_HANDOFF_KEY = 'wechat_qr_handoff'

let wechatQrHandoffMemory = null
let wechatInfoRequestCache = null

export function getApiCode(response) {
  return response?.data?.code ?? response?.code
}

export function getApiMessage(response) {
  return response?.data?.msg ?? response?.msg
}

export function getApiPayload(response) {
  return response?.data?.data ?? response?.data ?? null
}

export function getErrorCode(error) {
  return error?.response?.data?.code ?? error?.data?.code ?? error?.code
}

export function createWechatInfoParams(scanId, search) {
  const params = new URLSearchParams(search || '')
  return {
    scan_id: scanId,
    code: params.get('code') || '',
    state: params.get('state') || '',
  }
}

export function saveWechatQrHandoff(scanId, info) {
  const handoff = {
    scan_id: scanId,
    status: info?.status,
    code_type: info?.code_type,
    confirm_token: info?.confirm_token,
    registration_token: info?.registration_token,
  }
  wechatQrHandoffMemory = handoff
  window.sessionStorage.setItem(WECHAT_QR_HANDOFF_KEY, JSON.stringify(handoff))
  return handoff
}

export function getWechatQrHandoff() {
  if (wechatQrHandoffMemory) return wechatQrHandoffMemory
  const value = window.sessionStorage.getItem(WECHAT_QR_HANDOFF_KEY)
  if (!value) return null
  try {
    wechatQrHandoffMemory = JSON.parse(value)
    return wechatQrHandoffMemory
  } catch (_) {
    window.sessionStorage.removeItem(WECHAT_QR_HANDOFF_KEY)
    return null
  }
}

export function consumeWechatQrHandoff() {
  const handoff = getWechatQrHandoff()
  window.sessionStorage.removeItem(WECHAT_QR_HANDOFF_KEY)
  return handoff
}

export function isWechatQrHandoffReady(handoff, scanId) {
  if (!handoff || handoff.scan_id !== scanId) return false
  if (handoff.status === 'verified') return Boolean(handoff.confirm_token)
  if (handoff.status === 'registration_required') {
    return Boolean(handoff.registration_token)
  }
  return false
}

export function requestWechatInfoOnce(scanId, requestFactory) {
  if (wechatInfoRequestCache?.scanId === scanId) {
    return wechatInfoRequestCache.promise
  }
  const promise = Promise.resolve().then(requestFactory)
  wechatInfoRequestCache = { scanId, promise }
  return promise
}

export function clearWechatInfoRequest(scanId) {
  if (!scanId || wechatInfoRequestCache?.scanId === scanId) {
    wechatInfoRequestCache = null
  }
}

export function clearWechatQrSession() {
  wechatQrHandoffMemory = null
  wechatInfoRequestCache = null
  window.sessionStorage.removeItem(WECHAT_QR_HANDOFF_KEY)
}

export function createQrPoller({
  poll,
  isComplete,
  onComplete,
  onExpired,
  onError,
  interval = 2500,
  now = () => Date.now(),
}) {
  let timer = null
  let generation = 0
  let pollToken = null
  let expiresAt = 0

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  const stop = () => {
    generation += 1
    clearTimer()
    pollToken = null
    expiresAt = 0
  }

  const expire = (run) => {
    if (run !== generation) return
    stop()
    onExpired?.()
  }

  const schedule = (run, tick) => {
    if (run !== generation || !pollToken) return
    const remaining = expiresAt - now()
    if (remaining <= 0) {
      expire(run)
      return
    }
    timer = setTimeout(tick, Math.min(interval, remaining))
  }

  const start = (token, expiresIn) => {
    stop()
    if (!token || !Number(expiresIn) || Number(expiresIn) <= 0) {
      onExpired?.()
      return
    }

    pollToken = token
    expiresAt = now() + Number(expiresIn) * 1000
    const run = generation

    const tick = async () => {
      timer = null
      if (run !== generation || !pollToken) return
      if (now() >= expiresAt) {
        expire(run)
        return
      }

      try {
        const response = await poll(pollToken)
        if (run !== generation) return
        const code = getApiCode(response)
        if (code === WECHAT_QR_EXPIRED_CODE) {
          expire(run)
          return
        }
        if (code && code !== 0) {
          onError?.(getApiMessage(response), code)
        } else {
          const data = getApiPayload(response)
          if (isComplete(data)) {
            stop()
            onComplete(data)
            return
          }
        }
      } catch (error) {
        if (run !== generation) return
        const code = getErrorCode(error)
        if (code === WECHAT_QR_EXPIRED_CODE) {
          expire(run)
          return
        }
        onError?.(error?.response?.data?.msg || error?.message, code)
      }

      schedule(run, tick)
    }

    schedule(run, tick)
  }

  return { start, stop }
}
